/**
 * Registry of every locale the UI ships translations for. Each entry pairs a
 * BCP-47 `code` with a `label` (used in admin/debug contexts) and a
 * `nativeLabel` (shown in user-facing language switchers, written in the
 * target language itself).
 *
 * Adding a new language is a three-step change:
 * 1. Append an entry here.
 * 2. Create `messages/<code>.ts` exporting a `Messages`-typed object.
 * 3. Register that file in `messages/index.ts`.
 */
export const SUPPORTED_UI_LANGUAGES = [
  { code: 'en-US', label: 'English', nativeLabel: 'English' },
  { code: 'id-ID', label: 'Indonesian', nativeLabel: 'Bahasa Indonesia' },
  {
    code: 'es-419',
    label: 'Spanish (Latin America)',
    nativeLabel: 'Español (Latinoamérica)',
  },
] as const;

/** BCP-47 language code of any locale listed in {@link SUPPORTED_UI_LANGUAGES}. */
export type UiLanguageCode = (typeof SUPPORTED_UI_LANGUAGES)[number]['code'];

/**
 * Locale used when no preference is known. Also the locale that
 * {@link useTranslation} falls back to when no `<I18nProvider>` is mounted.
 */
export const DEFAULT_UI_LANGUAGE: UiLanguageCode = 'en-US';

/**
 * Type guard that narrows an unknown value to a {@link UiLanguageCode}. Use
 * before passing a candidate code from an untyped source (localStorage, a
 * GraphQL response, URL params) to anything that expects a `UiLanguageCode`.
 */
export function isUiLanguageCode(value: unknown): value is UiLanguageCode {
  return (
    typeof value === 'string' &&
    SUPPORTED_UI_LANGUAGES.some((l) => l.code === value)
  );
}
